import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../../data/database';
import { CUT_TYPES, COUNTRIES, COUNTRY_CITIES, TRANSMISSIONS, DRIVE_SYSTEMS, STEER_OPTIONS, FUEL_TYPES, getYardLocations } from '../../data/constants';
import { useLanguage } from '../../translations/LanguageContext';
import { CheckIcon, UploadIcon, CameraIcon } from '../Icons';
import { logActivity } from '../../data/notifications';
import centralUpdateStatus from '../../utils/updateUnitStatus';

import VehicleExtraSpecs, { VEHICLE_CATEGORIES } from '../VehicleExtraSpecs';
import PartsSelector from '../PartsSelector';
import PartTypeSelect from '../PartTypeSelect';
import { PART_TYPES, getPartType, normalizeCutType, resolvePartTypeEntry } from '../../data/partTypes';

const CUT_OPTIONS = PART_TYPES.filter(pt => pt.tier === 'cut');

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'assigned', label: 'Assigned to Customer' },
  { value: 'sold', label: 'Sold' },
];

function AddModal({ type, onSave, onClose, lang, user, initialData = null }) {
  const { t } = useLanguage();
  const [data, setData]     = useState(initialData || { status: 'available', destination_yard: 'japan_yard' });
  const [images, setImages] = useState(initialData?.images || []);
  const [auctionSheets, setAuctionSheets] = useState(initialData?.auction_sheets || []);
  const isEdit = !!initialData;
  const fileRef   = useRef();
  const cameraRef = useRef();
  const auctionRef = useRef();

  const customers = DB.get('customers') || [];
  const [chassisDupe, setChassisDupe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPartsPicker, setShowPartsPicker] = useState(false);

  // All existing chassis numbers (for duplicate detection)
  const existingChassis = !isEdit ? [
    ...(DB.get('vehicles') || []),
    ...(DB.get('spareParts') || []),
  ].filter(u => u.chassis).map(u => ({ chassis: u.chassis.trim().toUpperCase(), name: u.make ? `${u.make} ${u.model}` : u.partName || '', sku: u.sku })) : [];

  const updateField = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
    // Check chassis duplicate
    if (field === 'chassis' && value.trim().length >= 10 && !isEdit) {
      const match = existingChassis.find(u => u.chassis === value.trim().toUpperCase());
      setChassisDupe(match || null);
    } else if (field === 'chassis') {
      setChassisDupe(null);
    }
  };

  const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      compressImage(file).then(compressed => {
        // Show preview immediately
        setImages(prev => [...prev, compressed]);
        // Upload to cloud in background, swap base64 → URL when done
        uploadImage(compressed).then(url => {
          if (url !== compressed) {
            setImages(prev => prev.map(src => src === compressed ? url : src));
          }
        }).catch(() => {});
      });
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) =>
    setImages(prev => prev.filter((_, i) => i !== idx));

  const moveImage = (idx, dir) =>
    setImages(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (chassisDupe && !isEdit) {
      alert(`This chassis number is already in the system: ${chassisDupe.name} (${chassisDupe.sku})`);
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...data, images, auction_sheets: auctionSheets });
    } finally {
      setSaving(false);
    }

    if (!isEdit) {
      let unitName = '', unitType = '';
      if (type === 'vehicles') {
        unitName = `${data.make || ''} ${data.model || ''}`.trim() || 'Vehicle';
        unitType = 'Vehicle';
      } else if (type === 'engines') {
        unitName = data.engineName || 'Engine';
        unitType = 'Engine';
      } else {
        unitName = data.partName || 'Part';
        unitType = 'Spare Part';
      }
      logActivity.unitAdded(unitType, unitName, user?.name || 'Staff', user?.role || null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {isEdit ? 'Edit' : t('add')}{' '}
            {type === 'vehicles' ? t('vehicles') : type === 'engines' ? t('engines') : t('parts')}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* ── Image Upload ── */}
            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Photos</label>

              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                {/* Upload from gallery / file system */}
                <div
                  className="upload-zone"
                  style={{ flex: 1, padding: '18px 12px' }}
                  onClick={() => fileRef.current.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <UploadIcon />
                  <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text2)' }}>
                    Click or drag to upload
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => handleFiles(e.target.files)}
                  />
                </div>

                {/* Camera capture */}
                <div
                  className="upload-zone"
                  style={{ flex: 1, padding: '18px 12px' }}
                  onClick={() => cameraRef.current.click()}
                >
                  <CameraIcon />
                  <div style={{ fontSize: 13, marginTop: 6, color: 'var(--text2)' }}>
                    Take a photo
                  </div>
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={e => handleFiles(e.target.files)}
                  />
                </div>
              </div>

              {/* Thumbnails — reorder with arrows */}
              {images.length > 0 && (
                <div className="upload-preview">
                  {images.map((src, i) => (
                    <div key={i} className="upload-thumb" style={{ position: 'relative' }}>
                      <img src={src} alt="" />
                      {i === 0 && <div style={{ position: 'absolute', top: 0, left: 0, background: 'var(--blue)', color: '#fff', fontSize: 8, padding: '1px 5px', borderRadius: '0 0 4px 0', fontWeight: 700 }}>1st</div>}
                      <button type="button" className="upload-thumb-rm" onClick={() => removeImage(i)}>×</button>
                      {/* Move to auction sheets */}
                      <button type="button" title="Move to Auction Sheets" onClick={e => { e.stopPropagation(); setAuctionSheets(prev => [...prev, src]); removeImage(i); }}
                        style={{ position: 'absolute', top: 0, right: 20, background: 'rgba(37,99,235,0.85)', color: '#fff', border: 'none', borderRadius: '0 0 0 4px', fontSize: 8, padding: '2px 4px', cursor: 'pointer', fontWeight: 700 }}>📄</button>
                      {images.length > 1 && (
                        <div style={{ position: 'absolute', bottom: 2, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 2 }}>
                          {i > 0 && (
                            <button type="button" onClick={e => { e.stopPropagation(); moveImage(i, -1); }}
                              style={{ width: 18, height: 18, border: 'none', borderRadius: 3, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              ‹
                            </button>
                          )}
                          {i < images.length - 1 && (
                            <button type="button" onClick={e => { e.stopPropagation(); moveImage(i, 1); }}
                              style={{ width: 18, height: 18, border: 'none', borderRadius: 3, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              ›
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Auction Sheets Upload ── */}
            <div style={{ marginBottom: 20 }}>
              <label className="form-label">Auction Sheets <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 10 }}>— portal customers only, NOT shown on website</span></label>
              <div
                className="upload-zone"
                style={{ padding: '14px 12px', marginBottom: 10 }}
                onClick={() => auctionRef.current.click()}
              >
                <span style={{ fontSize: 18 }}>📄</span>
                <div style={{ fontSize: 12, marginTop: 4, color: 'var(--text3)' }}>Upload auction sheet images</div>
                <input ref={auctionRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                  onChange={e => {
                    const files = Array.from(e.target.files);
                    files.forEach(file => {
                      if (!file.type.startsWith('image/')) return;
                      const reader = new FileReader();
                      reader.onload = ev => {
                        uploadImage(ev.target.result).then(url => {
                          if (url) setAuctionSheets(prev => [...prev, url]);
                          else setAuctionSheets(prev => [...prev, ev.target.result]);
                        }).catch(() => setAuctionSheets(prev => [...prev, ev.target.result]));
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }} />
              </div>
              {auctionSheets.length > 0 && (
                <div className="upload-preview">
                  {auctionSheets.map((src, i) => (
                    <div key={i} className="upload-thumb" style={{ position: 'relative' }}>
                      <img src={src} alt="" />
                      <div style={{ position: 'absolute', top: 0, left: 0, background: 'var(--blue)', color: '#fff', fontSize: 7, padding: '1px 5px', borderRadius: '0 0 4px 0', fontWeight: 700 }}>SHEET</div>
                      <button type="button" className="upload-thumb-rm" onClick={() => setAuctionSheets(prev => prev.filter((_, j) => j !== i))}>×</button>
                      {/* Move back to photos */}
                      <button type="button" title="Move to Photos" onClick={e => { e.stopPropagation(); setImages(prev => [...prev, src]); setAuctionSheets(prev => prev.filter((_, j) => j !== i)); }}
                        style={{ position: 'absolute', top: 0, right: 20, background: 'rgba(34,197,94,0.85)', color: '#fff', border: 'none', borderRadius: '0 0 0 4px', fontSize: 8, padding: '2px 4px', cursor: 'pointer', fontWeight: 700 }}>📷</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Vehicle Fields ── */}
            {type === 'vehicles' && (
              <>
                {/* Vehicle Category */}
                <div className="form-row" style={{ gridTemplateColumns: '1fr', marginBottom: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Vehicle Category</label>
                    <select className="form-select" value={data.vehicleCategory || 'passenger_car'} onChange={e => updateField('vehicleCategory', e.target.value)}>
                      {VEHICLE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('chassis')} <span className="req">*</span></label>
                    <input className="form-input" required value={data.chassis || ''} onChange={e => updateField('chassis', e.target.value)}
                      style={chassisDupe ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(248,81,73,0.15)' } : undefined} />
                    {chassisDupe && (
                      <div style={{ marginTop: 6, padding: '8px 12px', background: 'rgba(248,81,73,0.1)', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--red)' }}>
                        ⚠ This chassis is already in the system — <strong>{chassisDupe.name}</strong> ({chassisDupe.sku})
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('make')} <span className="req">*</span></label>
                    <input className="form-input" required value={data.make || ''} onChange={e => updateField('make', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('model')} <span className="req">*</span></label>
                    <input className="form-input" required value={data.model || ''} onChange={e => updateField('model', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('year')}</label>
                    <input type="number" className="form-input" value={data.year || ''} onChange={e => updateField('year', parseInt(e.target.value) || '')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Engine</label>
                    <input className="form-input" placeholder="e.g. 2JZ-GTE, RB26" value={data.engineCode || ''} onChange={e => updateField('engineCode', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('displacement')}</label>
                    <input className="form-input" value={data.displacement || ''} onChange={e => updateField('displacement', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('mileage')}</label>
                    <input type="number" className="form-input" value={data.mileage || ''} onChange={e => updateField('mileage', parseInt(e.target.value) || '')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Score</label>
                    <input className="form-input" placeholder="e.g. 4.5, R" value={data.score || ''} onChange={e => updateField('score', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Grade</label>
                    <input className="form-input" placeholder="e.g. G, X Limited, 40th Anniversary" value={data.grade || ''} onChange={e => updateField('grade', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Fuel</label>
                    <select className="form-select" value={data.fuel || ''} onChange={e => updateField('fuel', e.target.value)}>
                      <option value="">Select</option>
                      {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('transmission')}</label>
                    <select className="form-select" value={data.transmission || ''} onChange={e => updateField('transmission', e.target.value)}>
                      <option value="">Select</option>
                      {TRANSMISSIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Drive System</label>
                    <select className="form-select" value={data.driveSystem || ''} onChange={e => updateField('driveSystem', e.target.value)}>
                      <option value="">Select</option>
                      {DRIVE_SYSTEMS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('color')}</label>
                    <input className="form-input" value={data.color || ''} onChange={e => updateField('color', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Steering</label>
                    <select className="form-select" value={data.steer || ''} onChange={e => updateField('steer', e.target.value)}>
                      <option value="">Select</option>
                      {STEER_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Seats</label>
                    <input className="form-input" placeholder="e.g. 5, 7, 2+2" value={data.seats || ''} onChange={e => updateField('seats', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select className="form-select" value={data.country || ''} onChange={e => { updateField('country', e.target.value); updateField('city', ''); }}>
                      <option value="">Select</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input className="form-input" placeholder="e.g. Yard A, Warehouse 3" value={data.location || ''} onChange={e => updateField('location', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <select className="form-select" value={data.city || ''} onChange={e => updateField('city', e.target.value)} disabled={!data.country}>
                      <option value="">{data.country ? 'Select city' : 'Select country first'}</option>
                      {(COUNTRY_CITIES[data.country] || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                {/* Cut type — shown for both new and edit on vehicles tab */}
                <div className="form-row" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="form-group">
                    <label className="form-label">Unit Type</label>
                    <select className="form-select" value={data.cutType ? normalizeCutType(data.cutType) : (() => { const r = resolvePartTypeEntry(data); return r.tier === 'cut' ? r.id : ''; })()} onChange={e => updateField('cutType', e.target.value)}>
                      <option value="">Full Vehicle</option>
                      {CUT_OPTIONS.map(pt => <option key={pt.id} value={pt.id}>{pt.display}</option>)}
                    </select>
                    {data.cutType && (
                      <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 4 }}>
                        This will be saved as a Part, not a full vehicle.
                      </div>
                    )}
                  </div>
                </div>

                {/* Dynamic extra specs based on vehicle category */}
                <VehicleExtraSpecs
                  category={data.vehicleCategory || 'passenger_car'}
                  extraSpecs={data.extraSpecs || {}}
                  onChange={specs => updateField('extraSpecs', specs)}
                />
              </>
            )}

            {/* ── Engine Fields ── */}
            {type === 'engines' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Engine Name / Code <span className="req">*</span></label>
                    <input className="form-input" required value={data.engineName || ''} onChange={e => updateField('engineName', e.target.value)} placeholder="e.g. 1GD-FTV, 2JZ-GTE" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('displacement')}</label>
                    <input className="form-input" value={data.displacement || ''} onChange={e => updateField('displacement', e.target.value)} placeholder="e.g. 2800cc" />
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 8 }}>Source Vehicle (which car is this engine from)</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('make')}</label>
                    <input className="form-input" value={data.make || data.manufacturer || ''} onChange={e => { updateField('make', e.target.value); updateField('manufacturer', e.target.value); }} placeholder="e.g. Toyota" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('model')}</label>
                    <input className="form-input" value={data.model || ''} onChange={e => updateField('model', e.target.value)} placeholder="e.g. Hilux" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('chassis')}</label>
                    <input className="form-input" value={data.chassis || ''} onChange={e => updateField('chassis', e.target.value)} placeholder="Source chassis (optional)" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Fuel</label>
                    <select className="form-select" value={data.fuel || ''} onChange={e => updateField('fuel', e.target.value)}>
                      <option value="">Select</option>
                      {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('mileage')}</label>
                    <input type="number" className="form-input" value={data.mileage || ''} onChange={e => updateField('mileage', parseInt(e.target.value) || '')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('condition')}</label>
                    <select className="form-select" value={data.condition || ''} onChange={e => updateField('condition', e.target.value)}>
                      <option value="">Select</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select className="form-select" value={data.country || ''} onChange={e => { updateField('country', e.target.value); updateField('city', ''); }}>
                      <option value="">Select</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* ── Parts Fields — images already shown above, details first, then part type ── */}
            {type === 'parts' && (
              <>
                {/* Source vehicle details FIRST */}
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 8 }}>Source Vehicle (which car is this part from)</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('make')}</label>
                    <input className="form-input" value={data.make || ''} onChange={e => updateField('make', e.target.value)} placeholder="e.g. Toyota" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('model')}</label>
                    <input className="form-input" value={data.model || ''} onChange={e => updateField('model', e.target.value)} placeholder="e.g. Hilux" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('chassis')}</label>
                    <input className="form-input" value={data.chassis || data.fitment || ''} onChange={e => { updateField('chassis', e.target.value); updateField('fitment', e.target.value); }} placeholder="Source chassis (optional)" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Year</label>
                    <input type="number" className="form-input" value={data.year || ''} onChange={e => updateField('year', parseInt(e.target.value) || '')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fuel</label>
                    <select className="form-select" value={data.fuel || ''} onChange={e => updateField('fuel', e.target.value)}>
                      <option value="">Select</option>
                      {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('mileage')}</label>
                    <input type="number" className="form-input" value={data.mileage || ''} onChange={e => updateField('mileage', parseInt(e.target.value) || '')} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Engine</label>
                    <input className="form-input" placeholder="e.g. 2JZ-GTE, RB26" value={data.engineCode || ''} onChange={e => updateField('engineCode', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('displacement')}</label>
                    <input className="form-input" value={data.displacement || ''} onChange={e => updateField('displacement', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Score</label>
                    <input className="form-input" placeholder="e.g. 4.5, R" value={data.score || ''} onChange={e => updateField('score', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Grade</label>
                    <input className="form-input" placeholder="e.g. G, X Limited" value={data.grade || ''} onChange={e => updateField('grade', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('transmission')}</label>
                    <select className="form-select" value={data.transmission || ''} onChange={e => updateField('transmission', e.target.value)}>
                      <option value="">Select</option>
                      {TRANSMISSIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Drive System</label>
                    <select className="form-select" value={data.driveSystem || ''} onChange={e => updateField('driveSystem', e.target.value)}>
                      <option value="">Select</option>
                      {DRIVE_SYSTEMS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('color')}</label>
                    <input className="form-input" value={data.color || ''} onChange={e => updateField('color', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Steering</label>
                    <select className="form-select" value={data.steer || ''} onChange={e => updateField('steer', e.target.value)}>
                      <option value="">Select</option>
                      {STEER_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Seats</label>
                    <input className="form-input" placeholder="e.g. 5, 7, 2+2" value={data.seats || ''} onChange={e => updateField('seats', e.target.value)} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('condition')}</label>
                    <select className="form-select" value={data.condition || ''} onChange={e => updateField('condition', e.target.value)}>
                      <option value="">Select</option>
                      <option value="New">New</option>
                      <option value="Used">Used</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('quantity')}</label>
                    <input type="number" className="form-input" value={data.quantity || 1} onChange={e => updateField('quantity', parseInt(e.target.value) || 1)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select className="form-select" value={data.country || ''} onChange={e => { updateField('country', e.target.value); updateField('city', ''); }}>
                      <option value="">Select</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input className="form-input" placeholder="e.g. Yard A, Warehouse 3" value={data.location || ''} onChange={e => updateField('location', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <select className="form-select" value={data.city || ''} onChange={e => updateField('city', e.target.value)} disabled={!data.country}>
                      <option value="">{data.country ? 'Select city' : 'Select country first'}</option>
                      {(COUNTRY_CITIES[data.country] || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group" />
                </div>

                {/* Part type selection AFTER details */}
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Part Type</label>
                    <PartTypeSelect
                      value={data.partType || ''}
                      onChange={v => updateField('partType', v)}
                      vehicleClass={data.vehicleClass || 'car'}
                      onVehicleClassChange={v => updateField('vehicleClass', v)}
                      allowCustom={false}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Part Name / Description <span className="req">*</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="form-input" required value={data.partName || ''} onChange={e => updateField('partName', e.target.value)} placeholder="Type or select from catalog" style={{ flex: 1 }} />
                      <button type="button" className="btn btn-secondary btn-sm" style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => setShowPartsPicker(true)}>
                        Browse Catalog
                      </button>
                    </div>
                    {data.partName && data.category && (
                      <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 4 }}>Category: {data.category}</div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label className="form-label">Description</label>
                    <textarea className="form-input" rows={2} value={data.description || ''} onChange={e => updateField('description', e.target.value)} placeholder="e.g. Front bumper, white, good condition" style={{ resize: 'vertical' }} />
                  </div>
                </div>

                {/* Cut details — only for cut-type parts */}
                {data.category && ['Cut Types', 'Half Cut', 'Nose Cut', 'Rear Cut', 'Front Cut', 'CKD', 'SKD', 'CBU', 'CKD W/O Engine'].some(ct => (data.category || '').includes(ct) || (data.partName || '').includes(ct)) && (
                  <div style={{ marginTop: 12, padding: 14, background: 'var(--bg3)', borderRadius: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 10 }}>What's included in this cut?</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {[
                        ['doors_included', 'Doors'],
                        ['back_trunk', 'Back trunk'],
                        ['brake_lights', 'Brake lights'],
                        ['back_differential', 'Back differential'],
                        ['back_shocks', 'Back shocks'],
                        ['back_bumper', 'Back bumper'],
                        ['front_bumper', 'Front bumper'],
                        ['front_shocks', 'Front shocks'],
                        ['radiator', 'Radiator'],
                        ['ac_compressor', 'AC compressor'],
                        ['steering_rack', 'Steering rack'],
                        ['dashboard', 'Dashboard'],
                        ['seats', 'Seats'],
                      ].map(([key, label]) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!(data.cut_details || {})[key]} onChange={e => updateField('cut_details', { ...(data.cut_details || {}), [key]: e.target.checked })} style={{ accentColor: 'var(--green)' }} />
                          {label}
                          {key === 'doors_included' && (data.cut_details || {}).doors_included && (
                            <select value={(data.cut_details || {}).doors_quantity || 4} onChange={e => updateField('cut_details', { ...(data.cut_details || {}), doors_quantity: parseInt(e.target.value) })} style={{ marginLeft: 4, padding: '1px 4px', borderRadius: 4, border: '1px solid var(--border)', fontSize: 11, background: 'var(--bg2)' }}>
                              {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          )}
                        </label>
                      ))}
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 12 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="radio" name="catalyst" checked={(data.cut_details || {}).with_catalyst === true} onChange={() => updateField('cut_details', { ...(data.cut_details || {}), with_catalyst: true })} /> With catalyst
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="radio" name="catalyst" checked={(data.cut_details || {}).with_catalyst === false} onChange={() => updateField('cut_details', { ...(data.cut_details || {}), with_catalyst: false })} /> Without catalyst
                      </label>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <label className="form-label" style={{ fontSize: 11 }}>Additional parts included</label>
                      <input className="form-input" value={(data.cut_details || {}).additional_parts || ''} onChange={e => updateField('cut_details', { ...(data.cut_details || {}), additional_parts: e.target.value })} placeholder="e.g. AW付きタイヤ積み込み" />
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Status ── */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('status')}</label>
                  <select
                    className="form-select"
                    value={data.status}
                    onChange={e => {
                      updateField('status', e.target.value);
                      if (e.target.value !== 'assigned') updateField('assignedTo', '');
                    }}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Destination Yard</label>
                  <select className="form-select" value={data.destination_yard || ''} onChange={e => updateField('destination_yard', e.target.value)}>
                    <option value="">— Not assigned —</option>
                    {getYardLocations().map(y => <option key={y.id} value={y.id}>{y.flag} {y.name}</option>)}
                  </select>
                </div>

                {/* Customer picker — only when assigned */}
                {data.status === 'assigned' && (
                  <div className="form-group">
                    <label className="form-label">Assign to Customer</label>
                    <select
                      className="form-select"
                      value={data.assignedTo || ''}
                      onChange={e => updateField('assignedTo', e.target.value)}
                    >
                      <option value="">Select customer...</option>
                      {customers.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.company ? ` — ${c.company}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* ── Website Visibility (admin only) ── */}
            {(user?.role === 'admin' || user?.role === 'head_admin') && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 16, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={data.show_on_website || false} onChange={e => updateField('show_on_website', e.target.checked)} />
                  Show on Website
                </label>
                {data.show_on_website && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
                    <input type="checkbox" checked={data.is_featured || false} onChange={e => updateField('is_featured', e.target.checked)} />
                    Featured
                  </label>
                )}
              </div>
            )}

            {/* ── Purchase Info ── */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Purchase Info</div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Purchase Price (¥)</label>
                  <input type="number" className="form-input" value={data.costJPY || ''} onChange={e => updateField('costJPY', parseInt(e.target.value) || '')} placeholder="e.g. 380000" />
                </div>
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <select className="form-select" value={data.purchase_source || ''} onChange={e => updateField('purchase_source', e.target.value)}>
                    <option value="">Select...</option>
                    <option value="auction">Auction</option>
                    <option value="supplier">Supplier</option>
                    <option value="direct">Direct Purchase</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <input className="form-input" list="supplier-list" value={data.purchase_supplier || ''} onChange={e => updateField('purchase_supplier', e.target.value)} placeholder="e.g. USS Tokyo, TAA Nagoya" />
                  <datalist id="supplier-list">
                    {[...new Set([...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])].map(u => u.purchase_supplier).filter(Boolean))].sort().map(s => <option key={s} value={s} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label className="form-label">Lot Number</label>
                  <input className="form-input" value={data.lot_number || ''} onChange={e => updateField('lot_number', e.target.value)} placeholder="Optional" />
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            {/* Convert to Part — admin only, edit mode, vehicles */}
            {isEdit && type === 'vehicles' && (user?.role === 'admin' || user?.role === 'head_admin') && data.status !== 'converted' && (
              <button type="button" className="btn btn-secondary" style={{ marginRight: 'auto', fontSize: 11 }} onClick={() => {
                const ptEntry = (data.cutType ? getPartType(normalizeCutType(data.cutType)) : null) || resolvePartTypeEntry(data);
                const cutName = ptEntry.display;
                if (!window.confirm(`Convert "${data.make || ''} ${data.model || ''}" to a part (${cutName})?\n\nThe vehicle will be marked as converted and a new part record will be created.`)) return;

                const partsList = DB.get('spareParts') || [];
                const makeCode = (data.make || 'UNK').substring(0, 3).toUpperCase();
                const d = new Date();
                const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
                const newSku = `PRT-${makeCode}-${dateCode}-${String(partsList.length + 1).padStart(3, '0')}`;
                const newId = DB.genId('PRT');

                const newPart = {
                  id: newId, sku: newSku,
                  partName: `${data.make || ''} ${data.model || ''} — ${cutName}`.trim(),
                  partType: ptEntry.id, category: cutName, vehicleClass: 'car',
                  condition: data.condition || 'Used',
                  make: data.make, model: data.model, year: data.year,
                  chassis: data.chassis, fitment: data.chassis,
                  country: data.country, city: data.city,
                  images: images || [], notes: `Converted from vehicle ${data.sku || initialData?.sku || ''}`,
                  pricing: data.pricing, costJPY: data.costJPY,
                  purchase_supplier: data.purchase_supplier, purchase_source: data.purchase_source,
                  yard_location: data.yard_location,
                  status: 'available', assignedTo: data.assignedTo || null,
                  quantity: 1, daysInStock: 0, createdAt: new Date().toISOString().split('T')[0],
                };
                DB.set('spareParts', [...partsList, newPart]);

                centralUpdateStatus(initialData?.id || data.id, 'vehicle', 'converted', { extraFields: { convertedTo: newSku } });

                const stock = DB.get('customerStock') || [];
                DB.set('customerStock', stock.map(s => s.unitId === (initialData?.id || data.id) && s.unitType === 'vehicle' ? { ...s, unitId: newId, unitType: 'spare_part' } : s));

                alert(`✅ Converted!\n\nOld: ${data.sku || initialData?.sku} (Vehicle) → converted\nNew: ${newSku} (${cutName}) → active in Parts`);
                onClose();
              }}>
                🔄 Convert to Part
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <CheckIcon />
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : t('save')}
            </button>
          </div>
        </form>

        {/* Parts Catalog Selector (overlay) */}
        {showPartsPicker && (
          <PartsSelector
            mode="inventory"
            onCancel={() => setShowPartsPicker(false)}
            onConfirm={(parts) => {
              if (parts.length > 0) {
                updateField('partName', parts[0].part_name);
                updateField('category', parts[0].category);
              }
              setShowPartsPicker(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default AddModal;
